import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@xtnd/web/shared/api';

export interface BulkJobRecord {
  jobId: string;
  tenantId: string;
  action: string;
  status: 'queued' | 'dispatching' | 'processing' | 'complete' | 'failed';
  totalCount: number;
  acceptedCount: number;
  ineligibleCount: number;
  processedCount: number;
  failedCount: number;
  createdAt: string;
  updatedAt: string;
  downloadUrl?: string;
  downloadFilename?: string;
  downloadUrlExpiresAt?: string;
  mergedCount?: number;
  pageCount?: number;
  failures?: Array<{ juniperId: string; error: string; failedAt: string }>;
}

export const bulkJobKeys = {
  all: ['bulk-jobs'] as const,
  detail: (jobId: string) => [...bulkJobKeys.all, jobId] as const,
};

/**
 * Tiered backoff schedule for bulk-job polling.
 *
 * Total wall-clock budget: **~35 minutes**, which covers a 1000-row bulk PDF
 * regenerate (~8–16 min) or a 1000-row bulk send (~2–5 min) with significant
 * headroom. After the budget, polling stops and the UI relies on the next
 * regular list refresh to pick up the final state.
 *
 *   tier 1 (snappy):    12 polls @ 5s   →  60s
 *   tier 2 (medium):    24 polls @ 15s  →  6m  (cumulative 7m)
 *   tier 3 (relaxed):   56 polls @ 30s  →  28m (cumulative 35m)
 *
 * Override via `intervalMs` + `maxAttempts` if a specific call site needs
 * something different.
 */
function nextIntervalMs(attemptCount: number): number {
  if (attemptCount < 12) return 5000;
  if (attemptCount < 36) return 15000;
  if (attemptCount < 92) return 30000;
  return 0; // stop
}

/**
 * Bounded polling for a bulk job's status. Pings GET /juniper/bulk-jobs/:jobId
 * with tiered backoff (see `nextIntervalMs`) and stops when the job is terminal
 * ('complete' | 'failed') OR the ~35-minute wall-clock budget is exhausted.
 * The hook invalidates the invoices/TRR list once the job goes terminal so
 * the row badges update on their own.
 *
 * Pass `endpoint: 'juniper-trr-transactions'` to poll the TRR variant.
 *
 * For overrides, set `intervalMs` (fixed interval) AND `maxAttempts` together
 * — the fixed-interval branch only kicks in when `intervalMs` is provided.
 */
export function useBulkJobPolling(
  jobId: string | null | undefined,
  options: {
    intervalMs?: number;
    maxAttempts?: number;
    includeFailures?: boolean;
    endpoint?: 'juniper' | 'juniper-trr-transactions';
    onTerminal?: (job: BulkJobRecord) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  const fixedIntervalMs = options.intervalMs;
  const fixedMaxAttempts = options.maxAttempts;
  const endpoint = options.endpoint ?? 'juniper';
  const includeFailures = options.includeFailures ?? false;

  return useQuery({
    queryKey: bulkJobKeys.detail(jobId || ''),
    queryFn: async ({ signal }) => {
      const path = `/${endpoint}/bulk-jobs/${jobId}${includeFailures ? '?includeFailures=true' : ''}`;
      const job = await api.get<BulkJobRecord>(path, { signal });
      // Invalidate row data on every poll, not only on terminal. The dispatcher
      // claims processingAction per-id sequentially in a 100-300ms-per-item loop
      // *after* the API returns 202 to the UI — so the initial mutation-success
      // refetch only sees the first few rows badged. Polling-tick invalidations
      // let the list catch up as the dispatcher finishes claiming and as per-item
      // processors complete and clear processingAction.
      //
      // Keys must match the lists' actual queryKey prefixes:
      //   invoiceKeys.all === ['invoices']
      //   trustRequestTransactionKeys.all === ['trust-request-transactions']
      // (TRR uses kebab-case — camelCase here was a silent no-op.)
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['trust-request-transactions'] });
      if (job.status === 'complete' || job.status === 'failed') {
        options.onTerminal?.(job);
      }
      return job;
    },
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data as BulkJobRecord | undefined;
      if (data?.status === 'complete' || data?.status === 'failed') return false;
      const attemptCount = query.state.dataUpdateCount || 0;
      if (fixedIntervalMs != null) {
        if (fixedMaxAttempts != null && attemptCount >= fixedMaxAttempts) return false;
        return fixedIntervalMs;
      }
      const next = nextIntervalMs(attemptCount);
      return next === 0 ? false : next;
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}
